import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { Container, Eyebrow, Display, Lead, useReveal } from "./ui";
import { colors, fonts, media, prefersReducedMotion } from "../../styles/theme";
import { contrast, speedGap, retention } from "../../pages/landingContent";

// Crutch vs trellis: from one starting point, a warm "reliance" line sinks while a
// vine grows upward, both drawn as you scroll.

const VIEW_W = 1200;
const VIEW_H = 560;
const START = { x: 70, y: 300 };
const DOWN_PATH = "M70 300 C260 300 380 330 520 380 S820 470 1130 500";
const UP_PATH =
  "M70 300 C200 300 250 250 330 230 C430 205 420 150 520 150 C620 150 600 230 700 210 C800 190 780 110 870 90 C960 70 980 120 1050 80 C1090 58 1110 50 1130 44";
const STOP_T = [0.2, 0.45, 0.68, 0.9];
const LEAF_COUNT = 16;
const LEAF = "M0 0 C6 -11 20 -12 28 0 C20 12 6 11 0 0Z";

const Wrap = styled.section`
  padding: 40px 0 140px;
`;

const Head = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 40px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const Stage = styled.div`
  position: relative;
  border-radius: 32px;
  background:
    radial-gradient(60% 70% at 90% 5%, rgba(163, 177, 143, 0.35), transparent 70%),
    radial-gradient(50% 60% at 85% 100%, rgba(216, 168, 137, 0.35), transparent 70%),
    ${colors.linen};
  border: 1px solid ${colors.stone};
  padding: 20px 12px;
  overflow: hidden;

  @media ${media.medium} {
    padding: 40px 32px;
  }

  svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }
`;

const Plot = styled.div`
  position: relative;
`;

const Axis = styled.span`
  position: absolute;
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.pebble};
`;

// Positioned by its top-left corner (see placeLabels), clear of the lines and leaves.
const StopLabel = styled.span`
  position: absolute;
  white-space: nowrap;
  padding: 6px 12px;
  border-radius: 999px;
  font-family: ${fonts.body};
  font-size: 14px;
  font-weight: 600;
  z-index: 2;
  color: ${(p) => (p.$down ? colors.terracotta : colors.forest)};
  background: ${(p) => (p.$down ? "#F6E8DE" : "#E6EBDD")};
  border: 1px solid ${(p) => (p.$down ? "rgba(154, 78, 46, 0.35)" : "rgba(79, 100, 69, 0.4)")};
  box-shadow: 0 6px 14px -8px rgba(42, 35, 30, 0.45);

  @media (max-width: 659px) {
    display: none;
  }
`;

const Legend = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 24px;

  @media ${media.small} {
    grid-template-columns: 1fr 1fr;
  }
`;

const LegendItem = styled.div`
  padding: 18px 20px;
  border-radius: 18px;
  background: ${(p) => (p.$up ? "rgba(163, 177, 143, 0.22)" : "rgba(216, 168, 137, 0.2)")};

  strong {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ${fonts.display};
    font-weight: 400;
    font-size: 24px;
    letter-spacing: -0.02em;
    color: ${(p) => (p.$up ? colors.forest : colors.terracotta)};
  }
  strong::before {
    content: "";
    width: 28px;
    height: 3px;
    border-radius: 3px;
    background: ${(p) => (p.$up ? colors.moss : colors.terracotta)};
  }
  p {
    margin-top: 6px;
    font-family: ${fonts.body};
    font-size: 15px;
    color: ${colors.bark};
  }
`;

// Label size estimate (px); clearance and dot gap in view units.
const LABEL_H = 32;
const labelWidth = (text) => text.length * 8.2 + 28;
const CLEAR = 14;
const GAP = 26;
const NEAR_DOT = 40;

// For each stop, try positions around its dot and keep the one that stays inside the panel and
// touches the fewest line and leaf points, never overlapping dots or labels already placed.
// Line points right at the label's own dot are ignored (the line always passes through it);
// leaves never are. Earlier candidates win ties.
const LEAF_CLEAR = 10;
const placeLabels = (stops, texts, down, { lines, leaves }, scale, placed) =>
  stops.map((pt, i) => {
    const w = labelWidth(texts[i]) * scale;
    const h = LABEL_H * scale;
    const g = GAP;
    const above = { x: pt.x - w / 2, y: pt.y - g - h };
    const below = { x: pt.x - w / 2, y: pt.y + g };
    const lift = g * 1.9;
    const near = [
      { x: pt.x - w + 10, y: pt.y - g - h },
      { x: pt.x - 10, y: pt.y - g - h },
      { x: pt.x - 10, y: pt.y + g },
      { x: pt.x - w + 10, y: pt.y + g },
      { x: pt.x + g, y: pt.y - h / 2 },
      { x: pt.x - w - g, y: pt.y - h / 2 },
    ];
    // Vine labels prefer the space above the vine (a little higher if needed); line labels
    // prefer below.
    const candidates = down
      ? [below, above, ...near]
      : [above, ...near.slice(0, 2), { x: pt.x - w / 2, y: pt.y - lift - h }, { x: pt.x - w + 10, y: pt.y - lift - h }, { x: pt.x - 10, y: pt.y - lift - h }, below, ...near.slice(2)];
    const inBox = (c, q, pad) => q.x > c.x - pad && q.x < c.x + w + pad && q.y > c.y - pad && q.y < c.y + h + pad;
    const score = (c) => {
      if (c.x < 0 || c.x + w > VIEW_W || c.y < 0 || c.y + h > VIEW_H) return Infinity;
      // Keep each line's labels on its own side of the "today" baseline.
      if (down ? c.y < START.y + 8 : c.y + h > START.y - 8) return Infinity;
      if (stops.some((q) => inBox(c, q, 8))) return Infinity;
      if (placed.some((o) => c.x < o.x + o.w + 8 && c.x + w + 8 > o.x && c.y < o.y + o.h + 8 && c.y + h + 8 > o.y)) return Infinity;
      const lineHits = lines.filter((q) => Math.hypot(q.x - pt.x, q.y - pt.y) > NEAR_DOT && inBox(c, q, CLEAR)).length;
      const leafHits = leaves.filter((q) => inBox(c, q, LEAF_CLEAR)).length;
      return lineHits + leafHits * 4;
    };
    let best = candidates[0];
    let bestScore = Infinity;
    candidates.forEach((c) => {
      const sc = score(c);
      if (sc < bestScore) {
        best = c;
        bestScore = sc;
      }
    });
    const box = { ...best, w, h };
    placed.push(box);
    return { ...pt, box };
  });

const boxStyle = (b) => ({ left: `${(b.x / VIEW_W) * 100}%`, top: `${(b.y / VIEW_H) * 100}%` });

const Contrast = () => {
  const scope = useRef(null);
  const downRef = useRef(null);
  const upRef = useRef(null);
  const plotRef = useRef(null);
  const [geom, setGeom] = useState(null);
  const [labels, setLabels] = useState(null);
  useReveal(scope);

  // Measure the paths once to place stops and leaves along them.
  useLayoutEffect(() => {
    const down = downRef.current;
    const up = upRef.current;
    const downLen = down.getTotalLength();
    const upLen = up.getTotalLength();
    const at = (path, len, t) => {
      const p = path.getPointAtLength(len * t);
      return { x: p.x, y: p.y };
    };
    const leaves = Array.from({ length: LEAF_COUNT }, (_, i) => {
      const t = 0.06 + (i / (LEAF_COUNT - 1)) * 0.9;
      const a = at(up, upLen, t);
      const b = at(up, upLen, Math.min(1, t + 0.01));
      const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
      const side = i % 2 === 0 ? -1 : 1;
      return { ...a, t, rotate: angle + side * 50, scale: 0.8 + (i / LEAF_COUNT) * 0.7 };
    });
    const sample = (path, len) => Array.from({ length: 160 }, (_, k) => at(path, len, k / 159));
    setGeom({
      downLen,
      upLen,
      downStops: STOP_T.map((t) => ({ ...at(down, downLen, t), t })),
      upStops: STOP_T.map((t) => ({ ...at(up, upLen, t), t })),
      obstacles: {
        lines: [...sample(down, downLen), ...sample(up, upLen), START],
        // Points along each leaf (a leaf is ~28 units long and ~12 wide before scaling).
        leaves: leaves.flatMap((l) => {
          const r = (l.rotate * Math.PI) / 180;
          return [0, 7, 14, 21, 28].flatMap((d) =>
            [-6, 0, 6].map((o) => ({
              x: l.x + (Math.cos(r) * d - Math.sin(r) * o) * l.scale,
              y: l.y + (Math.sin(r) * d + Math.cos(r) * o) * l.scale,
            }))
          );
        }),
      },
      leaves,
    });
  }, []);

  // Place labels for the plot's current size (label text doesn't scale with the SVG).
  useLayoutEffect(() => {
    if (!geom || !geom.obstacles) return undefined;
    const layout = () => {
      const width = plotRef.current.offsetWidth;
      if (!width) return;
      const scale = VIEW_W / width;
      const placed = [];
      setLabels({
        up: placeLabels(geom.upStops, contrast.up.stops, false, geom.obstacles, scale, placed),
        down: placeLabels(geom.downStops, contrast.down.stops, true, geom.obstacles, scale, placed),
      });
    };
    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(plotRef.current);
    return () => ro.disconnect();
  }, [geom]);

  const labelsReady = Boolean(labels);
  useLayoutEffect(() => {
    if (!geom || !labelsReady) return undefined;
    const down = downRef.current;
    const up = upRef.current;
    down.style.strokeDasharray = `${geom.downLen}`;
    up.style.strokeDasharray = `${geom.upLen}`;
    if (prefersReducedMotion()) {
      down.style.strokeDashoffset = "0";
      up.style.strokeDashoffset = "0";
      return undefined;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        // Start when the first dot reaches the bottom of the screen; finish as the whole chart comes into view.
        scrollTrigger: {
          trigger: "[data-plot]",
          start: () => `top+=${(scope.current.querySelector("[data-plot]").offsetHeight * START.y) / VIEW_H} bottom`,
          end: "bottom 95%",
          scrub: 0.4,
          invalidateOnRefresh: true,
        },
      });
      tl.fromTo(down, { strokeDashoffset: geom.downLen }, { strokeDashoffset: 0, duration: 1 }, 0);
      tl.fromTo(up, { strokeDashoffset: geom.upLen }, { strokeDashoffset: 0, duration: 1 }, 0);
      gsap.utils.toArray("[data-leaf]").forEach((el) => {
        tl.fromTo(el, { scale: 0, svgOrigin: el.dataset.origin }, { scale: 1, duration: 0.06, ease: "back.out(2)" }, Math.min(0.94, Number(el.dataset.leaf) + 0.03));
      });
      gsap.utils.toArray("[data-stop]").forEach((el) => {
        tl.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.06 }, Number(el.dataset.stop) - 0.04);
      });
      const end = geom.upStops[3];
      tl.fromTo("[data-bloom]", { scale: 0, opacity: 0, svgOrigin: `${end.x} ${end.y}` }, { scale: 1, opacity: 1, duration: 0.06 }, 0.88);
    }, scope);
    return () => ctx.revert();
  }, [geom, labelsReady]);

  return (
    <Wrap ref={scope} aria-labelledby="contrast-title">
      <Container>
        <Head>
          <div data-reveal>
            <Eyebrow $color={colors.moss}>{contrast.eyebrow}</Eyebrow>
            <Display id="contrast-title" style={{ marginTop: 20 }}>
              {contrast.titleLead} <em>{contrast.titleEmphasis}</em>
            </Display>
          </div>
          <Lead data-reveal="0.1">{contrast.body}</Lead>
        </Head>

        <Stage data-reveal>
          <Plot data-plot ref={plotRef}>
            <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={`${contrast.down.label}: ${contrast.down.stops.join(", ")}. ${contrast.up.label}: ${contrast.up.stops.join(", ")}.`}>
              <defs>
                <linearGradient id="vine" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0" stopColor={colors.sage} />
                  <stop offset="0.6" stopColor={colors.moss} />
                  <stop offset="1" stopColor={colors.ocean} />
                </linearGradient>
                <radialGradient id="bloom">
                  <stop offset="0" stopColor={colors.copperLight} stopOpacity="0.9" />
                  <stop offset="1" stopColor={colors.copperLight} stopOpacity="0" />
                </radialGradient>
              </defs>
              <line x1="0" x2={VIEW_W} y1={START.y} y2={START.y} stroke={colors.stone} strokeDasharray="4 8" />
              <path ref={downRef} d={DOWN_PATH} fill="none" stroke={colors.terracotta} strokeWidth="3" strokeLinecap="round" opacity="0.8" />
              <path ref={upRef} d={UP_PATH} fill="none" stroke="url(#vine)" strokeWidth="5" strokeLinecap="round" />
              {geom &&
                geom.leaves.map((l, i) => (
                  <g key={i} transform={`translate(${l.x} ${l.y}) rotate(${l.rotate}) scale(${l.scale})`}>
                    <path data-leaf={l.t} data-origin={`${l.x} ${l.y}`} d={LEAF} fill={i % 3 === 0 ? colors.sage : colors.moss} opacity="0.9" />
                  </g>
                ))}
              {geom && (
                <g transform={`translate(${geom.upStops[3].x} ${geom.upStops[3].y})`}>
                  <circle data-bloom r="46" fill="url(#bloom)" />
                </g>
              )}
              {geom &&
                geom.downStops.map((p, i) => (
                  <circle key={`d${i}`} data-stop={p.t} cx={p.x} cy={p.y} r="7" fill={colors.linen} stroke={colors.terracotta} strokeWidth="3" />
                ))}
              {geom &&
                geom.upStops.map((p, i) => (
                  <circle key={`u${i}`} data-stop={p.t} cx={p.x} cy={p.y} r="8" fill={colors.linen} stroke={colors.moss} strokeWidth="3" />
                ))}
              <circle cx={START.x} cy={START.y} r="10" fill={colors.ink} />
            </svg>

            <Axis style={{ left: "1%", top: `${(START.y / VIEW_H) * 100 + 4}%` }}>Today</Axis>
            {labels &&
              labels.up.map((p, i) => (
                <StopLabel key={`ul${i}`} data-stop={p.t} style={boxStyle(p.box)}>
                  {contrast.up.stops[i]}
                </StopLabel>
              ))}
            {labels &&
              labels.down.map((p, i) => (
                <StopLabel key={`dl${i}`} data-stop={p.t} style={boxStyle(p.box)} $down>
                  {contrast.down.stops[i]}
                </StopLabel>
              ))}
          </Plot>

          <Legend>
            <LegendItem $up>
              <strong>{contrast.up.label}</strong>
              <p>{contrast.up.stops.join(" → ")}</p>
            </LegendItem>
            <LegendItem>
              <strong>{contrast.down.label}</strong>
              <p>{contrast.down.stops.join(" → ")}</p>
            </LegendItem>
          </Legend>
        </Stage>
      </Container>
    </Wrap>
  );
};

/* ---------- The AI-to-brain speed gap ---------- */

const GapWrap = styled.section`
  padding: 0 0 120px;
`;

const GapCard = styled.div`
  display: grid;
  gap: 36px;
  padding: 36px 26px;
  border-radius: 32px;
  background: ${colors.forest};
  color: ${colors.linen};

  @media ${media.medium} {
    padding: 44px 48px;
  }
  @media ${media.large} {
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
    align-items: center;
    gap: 56px;
  }
`;

const GapTitle = styled(Display)`
  font-size: clamp(36px, 4.2vw, 60px);
  margin: 18px 0 20px;
`;

const Rows = styled.div`
  display: grid;
  gap: 10px;
`;

const Row = styled.div`
  .line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 4px;
    font-family: ${fonts.body};
    font-size: 15px;
    color: ${(p) => (p.$hero ? colors.linen : "rgba(250, 247, 241, 0.72)")};
    font-weight: ${(p) => (p.$hero ? 600 : 400)};
  }
  sup {
    font-size: 10px;
    margin-left: 2px;
    color: rgba(250, 247, 241, 0.45);
  }
  .right {
    display: inline-flex;
    align-items: baseline;
    gap: 12px;
    flex: none;
  }
  /* Phones: tag and number drop to their own line under the label. */
  @media (max-width: 659px) {
    .line {
      flex-wrap: wrap;
      row-gap: 2px;
    }
    .right {
      width: 100%;
      justify-content: space-between;
    }
    .right > b {
      min-width: 0;
      margin-left: auto;
    }
  }
  .line b {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: ${(p) => (p.$hero ? "clamp(24px, 2.1vw, 30px)" : "clamp(18px, 1.6vw, 22px)")};
    letter-spacing: -0.02em;
    color: ${(p) => (p.$hero ? colors.copperLight : colors.linen)};
    white-space: nowrap;
  }
  /* Fixed-width numbers so the multiplier tags line up in a column. */
  .right > b {
    min-width: ${(p) => (p.$hero ? "auto" : "6.2em")};
    text-align: right;
  }
  .track {
    position: relative;
    height: ${(p) => (p.$hero ? "8px" : "4px")};
    border-radius: 8px;
    background: rgba(250, 247, 241, 0.07);
    overflow: hidden;
  }
  .fill,
  .range {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 8px;
    transform-origin: 0 50%;
    background: ${(p) => p.$fill};
  }
  .range {
    opacity: 0.4;
  }
  .headroom {
    position: absolute;
    top: 0;
    bottom: 0;
    border-radius: 8px;
    transform-origin: 0 50%;
    background: linear-gradient(90deg, rgba(209, 154, 102, 0.35), rgba(209, 154, 102, 0.08));
  }
`;

const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  vertical-align: 2px;
  font-family: ${fonts.mono};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.copperLight};
  border: 1px solid rgba(209, 154, 102, 0.45);

  b {
    font-family: ${fonts.mono} !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    color: ${colors.copperLight} !important;
    letter-spacing: 0 !important;
  }
`;

// "vimbal pace = N×" for each human row: vimbal's top pace over the group's typical speed.
const multiplier = (r, pace) => {
  const typical = r.min !== undefined ? (r.min + r.max) / 2 : r.max;
  const x = pace / typical;
  return x >= 10 ? Math.round(x) : Math.round(x * 10) / 10;
};

// AI output sits off this chart, so it is shown as a quiet line beyond the scale.
const Beyond = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed rgba(250, 247, 241, 0.2);

  .head {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: ${fonts.mono};
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(250, 247, 241, 0.55);
  }
  .head i {
    flex: 1;
    border-top: 1px dashed rgba(250, 247, 241, 0.3);
  }
  .head em {
    font-style: normal;
    letter-spacing: 0;
  }
  sup {
    font-size: 10px;
    margin-left: 2px;
    color: rgba(250, 247, 241, 0.45);
  }
  .items {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 28px;
    margin-top: 8px;
  }
  .items span {
    font-family: ${fonts.body};
    font-size: 15px;
    color: rgba(250, 247, 241, 0.72);
  }
  .items b {
    margin-left: 8px;
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: 22px;
    letter-spacing: -0.02em;
    color: ${colors.linen};
  }
`;

const Notes = styled.p`
  margin-top: 14px;
  font-family: ${fonts.body};
  font-size: 11px;
  line-height: 1.5;
  color: rgba(250, 247, 241, 0.42);
`;

const FILLS = {
  human: "rgba(250, 247, 241, 0.32)",
  average: "rgba(250, 247, 241, 0.6)",
  vimbal: `linear-gradient(90deg, ${colors.copper}, ${colors.copperLight})`,
};

export const SpeedGap = () => {
  const scope = useRef(null);
  useReveal(scope);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      gsap.from("[data-gap-fill]", {
        scaleX: 0,
        duration: 1.3,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: "[data-gap]", start: "top 80%", once: true },
      });
    }, scope);
    return () => ctx.revert();
  }, []);

  // One linear scale for all reading bars.
  const scaleMax = Math.max(...speedGap.rows.map((r) => r.max));
  const pct = (v) => `${(v / scaleMax) * 100}%`;

  return (
    <GapWrap ref={scope} aria-labelledby="gap-title">
      <Container>
        <GapCard data-reveal>
          <div>
            <Eyebrow $color={colors.sage} $onDark>
              {speedGap.eyebrow}
            </Eyebrow>
            <GapTitle id="gap-title" $onDark>
              {speedGap.titleLead} <em>{speedGap.titleEmphasis}</em>
            </GapTitle>
            <Lead $onDark>{speedGap.body}</Lead>
          </div>

          <div>
            <Rows data-gap>
              {speedGap.rows.map((r) => {
                const hero = r.kind === "vimbal";
                const mult = hero ? null : multiplier(r, speedGap.vimbalPace);
                return (
                  <Row key={r.label} $hero={hero} $fill={FILLS[r.kind]}>
                    <div className="line">
                      <span>
                        {r.label}
                        {r.note && <sup>{r.note}</sup>}
                      </span>
                      <span className="right">
                        {mult && (
                          <Tag>
                            {speedGap.multiplierLabel} <b>{mult}×</b>
                          </Tag>
                        )}
                        <b>{r.display}</b>
                      </span>
                    </div>
                    <div className="track">
                      {mult && (
                        <div
                          className="headroom"
                          data-gap-fill
                          style={{ left: pct(r.max), width: `calc(${pct(speedGap.vimbalPace)} - ${pct(r.max)})` }}
                        />
                      )}
                      {r.min !== undefined && <div className="range" data-gap-fill style={{ width: pct(r.max) }} />}
                      <div className="fill" data-gap-fill style={{ width: pct(r.min !== undefined ? r.min : r.max) }} />
                    </div>
                  </Row>
                );
              })}
            </Rows>

            <Beyond>
              <div className="head">
                {speedGap.beyond.title}
                <i aria-hidden="true" />
                <em aria-hidden="true">→</em>
              </div>
              <div className="items">
                {speedGap.beyond.items.map((it) => (
                  <span key={it.label}>
                    {it.label}
                    <sup>{it.note}</sup>
                    <b>{it.display}</b>
                  </span>
                ))}
              </div>
            </Beyond>

            <Notes>{speedGap.sources.join(" ")}</Notes>
          </div>
        </GapCard>
      </Container>
    </GapWrap>
  );
};

export default Contrast;

/* ---------- Retention: a pause after the speed card ---------- */

const RetentionWrap = styled.section`
  padding: 0 0 140px;
  text-align: center;
`;

const RetentionTitle = styled(Display)`
  margin-top: 18px;
  font-size: clamp(34px, 4vw, 56px);
`;

const BigFigure = styled.p`
  margin: 8px 0 4px;
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(110px, 16vw, 220px);
  line-height: 1;
  letter-spacing: -0.05em;
  color: ${colors.moss};
  font-variant-numeric: tabular-nums;
`;

const RetentionLabel = styled.p`
  max-width: 560px;
  margin: 0 auto;
  font-family: ${fonts.body};
  font-size: clamp(18px, 1.6vw, 22px);
  line-height: 1.45;
  color: ${colors.bark};
  text-wrap: balance;
`;

const RetentionSource = styled.p`
  margin-top: 18px;
  font-family: ${fonts.mono};
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.pebble};
`;

export const Retention = () => {
  const scope = useRef(null);
  const figureRef = useRef(null);
  useReveal(scope);
  const final = `${retention.prefix}${retention.value}${retention.suffix}`;

  // The figure counts up once as it comes into view.
  useLayoutEffect(() => {
    const el = figureRef.current;
    if (!el || prefersReducedMotion()) return undefined;
    const counter = { v: 0 };
    const ctx = gsap.context(() => {
      el.textContent = `${retention.prefix}0${retention.suffix}`;
      gsap.to(counter, {
        v: retention.value,
        duration: 2.2,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 85%", once: true },
        onUpdate: () => {
          el.textContent = `${retention.prefix}${Math.round(counter.v)}${retention.suffix}`;
        },
      });
    }, scope);
    return () => {
      ctx.revert();
      el.textContent = final;
    };
  }, [final]);

  return (
    <RetentionWrap ref={scope} aria-labelledby="retention-title">
      <Container>
        <div data-reveal>
          <Eyebrow $color={colors.moss}>{retention.eyebrow}</Eyebrow>
          <RetentionTitle id="retention-title">{retention.title}</RetentionTitle>
        </div>
        <BigFigure data-reveal="0.1" aria-label={final}>
          <span ref={figureRef} aria-hidden="true">
            {final}
          </span>
        </BigFigure>
        <RetentionLabel data-reveal="0.15">{retention.label}</RetentionLabel>
        <RetentionSource data-reveal="0.2">{retention.source}</RetentionSource>
      </Container>
    </RetentionWrap>
  );
};
